/**
 * Join lease/deed tracts to their deals for the deals map. Pure.
 *
 * Location lives on the tract, deal-level facts on the deal, so the map is
 * built from one row per tract:
 *
 *   tract.lease_deed_id -> lease_deeds.id
 *   lease_deeds.deal_id -> deals.id
 *   deals.brokerage_id  -> broker label (brokers_xref lookup)
 */
/**
 * @param {Array<object>} tracts      /broker_deals/lease_deed_tracts rows
 * @param {Array<object>} leaseDeeds  /broker_deals/lease_deeds rows
 * @param {Array<object>} deals       /broker_deals/deals rows
 * @param {object} [options]
 * @param {Record<string|number,string>} [options.brokerLabels]
 *   brokerage_id -> display name (useLookupLabels().labelByLookup.brokers_xref)
 * @returns {Array<object>} each tract's own fields plus, from its lease/deed,
 *   `lessor_grantor`; from its deal, `deal_id`, `deal_key`, `deal_status`,
 *   `status_chg_date`, `deal_type`, `total_bonus`,
 *   `deal_net_acres_delivered`, `brokerage_id` and `broker`; and
 *   `deal_tract_count` (how many tracts that deal has in the input).
 *   Tracts whose lease/deed or deal is not in the inputs are dropped — e.g. a
 *   deal outside an external broker's scope.
 */
export const joinTractRows = (tracts, leaseDeeds, deals, options = {}) => {
  const brokerLabels = options.brokerLabels || {};
  const dealsById = new Map();
  for (const d of Array.isArray(deals) ? deals : []) {
    if (d?.id !== null && d?.id !== undefined) dealsById.set(String(d.id), d);
  }
  const leaseDeedById = new Map();
  for (const ld of Array.isArray(leaseDeeds) ? leaseDeeds : []) {
    if (ld?.id !== null && ld?.id !== undefined) leaseDeedById.set(String(ld.id), ld);
  }

  const out = [];
  for (const t of Array.isArray(tracts) ? tracts : []) {
    const leaseDeed = leaseDeedById.get(String(t?.lease_deed_id));
    const dealId = leaseDeed?.deal_id;
    if (dealId === null || dealId === undefined) continue;
    const deal = dealsById.get(String(dealId));
    if (!deal) continue;
    const brokerageId = deal.brokerage_id ?? null;
    out.push({
      ...t,
      lessor_grantor: leaseDeed.lessor_grantor ?? null,
      deal_id: deal.id,
      deal_key: deal.deal_key ?? null,
      deal_status: deal.deal_status ?? null,
      status_chg_date: deal.status_chg_date ?? null,
      deal_type: deal.deal_type ?? null,
      total_bonus: deal.total_bonus ?? null,
      deal_net_acres_delivered: deal.deal_net_acres_delivered ?? null,
      brokerage_id: brokerageId,
      broker: brokerageId === null ? null : brokerLabels[brokerageId] ?? null,
    });
  }
  const perDeal = new Map();
  for (const r of out) perDeal.set(r.deal_id, (perDeal.get(r.deal_id) || 0) + 1);
  for (const r of out) r.deal_tract_count = perDeal.get(r.deal_id);
  return out;
};
